import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Story } from 'types';
import { DashboardRoutePath } from 'modules/dashboard/navigation';

import { Box, FlexContainer, Text } from '../../../../shared/Typhography';
import { TableComponent } from '../../../../shared/Components/Table/Table';
import { Button, Select, Splash } from '../../../../shared/Components';
import DatePickerInput from '../../../../shared/Components/DatePickerInput';
import { MODAL_TYPES, useGlobalModalContext } from '../../../../context/GlobalModal';
import {
  deleteStoryById,
  publishStoryById,
  unpublishStoryById,
} from '../../../../services/api/storiesApi';

import useStories from './hooks/useStories';
import { isAbleToPublishStory, isAbleToUnpublishStory, isAbleToDeleteStory } from './utils/helpers';
import { STATUS_FILTERS_OPTIONS, EXPIRATION_FILTERS_OPTIONS } from './contants';

const StoriesScreen = () => {
  const {
    data,
    isLoading,
    statusFilter,
    expirationFilter,
    fetch,
    handleSetStatusFilter,
    handleSetExpirationFilter,
    resetStatusFilter,
    resetExpirationFilter,
  } = useStories();

  const { showModal } = useGlobalModalContext();

  const history = useHistory();

  const dotRangeDays = useMemo(() => {
    const days: { startDate: Date; endDate: Date }[] = [];
    data?.forEach((item) => {
      const { publishDate, expirationDate } = item;
      const publishedDateAsDate = publishDate instanceof Date ? publishDate : publishDate?.toDate();
      const expirationDateAsDate =
        expirationDate instanceof Date ? expirationDate : expirationDate?.toDate();

      if (publishDate && expirationDate) {
        days.push({
          startDate: publishedDateAsDate as Date,
          endDate: expirationDateAsDate as Date,
        });
      }
    });
    return days;
  }, [data]);

  const columns = useMemo(
    () => [
      {
        Header: 'Story start - end date',
        accessor: 'startEndDate',
        Cell: (cell: any) => (
          <div style={{ marginLeft: 10 }}>
            <div>
              {cell?.row?.original?.publishDate?.toLocaleString('en-us', {
                month: 'short',
                year: 'numeric',
                day: 'numeric',
              }) || '/'}
            </div>
            <div>
              {cell?.row?.original?.expirationDate?.toLocaleString('en-us', {
                month: 'short',
                year: 'numeric',
                day: 'numeric',
              }) || '/'}
            </div>
          </div>
        ),
        width: '10%',
        disableSortBy: true,
      },
      {
        Header: 'Story ID',
        accessor: 'id',
        Cell: (cell: any) => <div>{cell?.row?.original?.id}</div>,
        disableSortBy: true,
        width: '7%',
      },
      {
        Header: 'Story Image',
        accessor: 'imageUrl',
        Cell: (cell: any) => <img width="100" height="80" src={cell?.row?.original?.imageUrl} />,
        disableSortBy: true,
        width: '5%',
      },
      {
        Header: 'Story Title',
        accessor: 'title',
        Cell: (cell: any) => <>{cell?.row?.original?.title}</>,
        disableSortBy: true,
        width: '10%',
      },
      {
        Header: 'Entry fee',
        accessor: 'entryFee',
        Cell: (cell: any) => {
          const entryFee = cell?.row?.original?.entryFee;
          let value = '';
          if (entryFee) {
            const entryFeeKeys = Object.keys(entryFee);
            if (entryFeeKeys.length) {
              const currency = entryFeeKeys[0];
              const amount = entryFee[currency];
              value = `${amount} ${currency}`;
            }
          }
          return <>{value}</>;
        },
        disableSortBy: true,
        width: '5%',
      },
      {
        Header: 'Chapters',
        accessor: 'chaptersCount',
        Cell: (cell: any) => <>{cell?.row?.original?.chaptersCount}</>,
        disableSortBy: true,
        width: '3%',
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: (cell: any) => <>{cell?.row?.original?.status}</>,
        disableSortBy: true,
        width: '3%',
      },
      {
        Header: '',
        accessor: 'actions',
        Cell: (cell: any) => (
          <>
            {isAbleToPublishStory(cell?.row?.original as Story) && (
              <Button
                variant="link"
                onClick={async () => {
                  const id = cell?.row?.original?.id;
                  if (!id) return;
                  await publishStoryById(id);
                  await fetch();
                }}
              >
                Publish
              </Button>
            )}
            {isAbleToUnpublishStory(cell?.row?.original as Story) && (
              <Button
                variant="link"
                onClick={async () => {
                  const id = cell?.row?.original?.id;
                  if (!id) return;
                  await unpublishStoryById(id);
                  await fetch();
                }}
              >
                Unpublish
              </Button>
            )}
            <Button
              variant="link"
              onClick={() => {
                history.push(`/${`${DashboardRoutePath.MANAGE_STORY}`}/${cell?.row?.original?.id}`);
              }}
            >
              Edit
            </Button>
            <Button
              variant="link"
              onClick={() => {
                showModal(MODAL_TYPES.DIALOG_MODAL, {
                  title: 'Delete Story?',
                  text: `Are you sure you want to delete this story?`,
                  cancelButtonText: 'Cancel',
                  submitButtonText: 'Delete',
                  onSubmit: async () => {
                    await deleteStoryById(cell?.row?.original?.id);
                    await fetch();
                  },
                });
              }}
              disabled={!isAbleToDeleteStory(cell?.row?.original as Story)}
            >
              Delete
            </Button>
          </>
        ),
        disableSortBy: true,
        width: '3%',
      },
    ],
    [],
  );

  return (
    <>
      <Box margin="150px auto 0">
        <FlexContainer justifyContent="space-between">
          <DatePickerInput
            selected={null}
            onChange={() => {}}
            inline={true}
            label="Date Range"
            dotRangeDays={dotRangeDays}
            readOnlyCalendar={true}
          />
          <FlexContainer
            flexDirection="column"
            justifyContent="space-between"
            alignItems="flex-end"
          >
            <Button
              height={60}
              width={150}
              onClick={() => history.push(`/${DashboardRoutePath.MANAGE_STORY}`)}
            >
              <span style={{ fontSize: '16px' }}>Create</span>
            </Button>
            <FlexContainer>
              <Select
                id="storiesFilterExpirationDate"
                label="All / Expiring / Non expiring"
                onChange={(e) => {
                  const value = e?.value;
                  resetExpirationFilter();
                  if (typeof value === 'undefined') return;
                  handleSetExpirationFilter(value as boolean);
                }}
                options={EXPIRATION_FILTERS_OPTIONS}
                value={
                  expirationFilter?.operator === '=='
                    ? EXPIRATION_FILTERS_OPTIONS[1]
                    : expirationFilter?.operator === '!='
                    ? EXPIRATION_FILTERS_OPTIONS[0]
                    : null
                }
                isClearable={true}
                style={{ width: '200px', marginRight: 20 }}
              />
              <Select
                id="storiesFilterStatus"
                label="All / Scheduled / Published / Draft / Expired / Unpublished"
                onChange={(e) => {
                  const value = e?.value;
                  resetStatusFilter();
                  if (typeof value === 'undefined') return;
                  handleSetStatusFilter(value as string);
                }}
                options={STATUS_FILTERS_OPTIONS}
                value={
                  STATUS_FILTERS_OPTIONS.find((filter) => filter.value === statusFilter?.value)
                    ? STATUS_FILTERS_OPTIONS.find((filter) => filter.value === statusFilter?.value)
                    : null
                }
                isClearable={true}
                style={{ width: '400px' }}
              />
            </FlexContainer>
          </FlexContainer>
        </FlexContainer>
      </Box>
      <Box margin="50px auto 150px">
        {isLoading ? (
          <Splash margin="40px 0 0 0" position="relative" />
        ) : data?.length === 0 ? (
          <Text textAlign="center">No results...</Text>
        ) : (
          <TableComponent
            loadingMore={false}
            fetchMore={false}
            columns={columns}
            data={data.map((item, index) => ({ index: index + 1, ...item }))}
          />
        )}
      </Box>
    </>
  );
};

export default StoriesScreen;
